import type { MessageTree, MessageValue, TranslationParams } from './types.ts';

export interface TranslateOptions {
  /** Backfill tree consulted when `key` is missing from the primary messages. */
  readonly fallback?: MessageTree;
  /** Params for `{placeholder}` interpolation. */
  readonly params?: TranslationParams;
  /**
   * How to render a key that is missing after fallback. Defaults to returning
   * the key verbatim; pass a marker (e.g. `k => `⟦${k}⟧`) for dev visibility.
   */
  readonly onMissing?: (key: string) => string;
}

/**
 * Pure translation lookup: resolve `key` (a dot path) against `messages`, then
 * `fallback`, interpolate `{placeholder}` tokens, and return the result. On a
 * miss after fallback, returns `onMissing(key)` (the key itself by default).
 *
 * No global state — callers own the loaded trees. Missing interpolation params
 * leave their `{token}` intact rather than injecting `undefined`.
 */
export function translate(
  messages: MessageTree,
  key: string,
  options: TranslateOptions = {},
): string {
  const raw = lookup(messages, key) ?? lookup(options.fallback, key);
  if (raw === undefined) {
    return options.onMissing ? options.onMissing(key) : key;
  }
  return interpolate(raw, options.params);
}

/**
 * Bind a translator to already-loaded messages. Returns a `t(key, params?)`
 * function — the shape app components consume. `K` can be narrowed to a
 * namespace's key union for typo-safe keys.
 */
export function createTranslator<K extends string = string>(
  messages: MessageTree,
  defaults: Pick<TranslateOptions, 'fallback' | 'onMissing'> = {},
): (key: K, params?: TranslationParams) => string {
  return (key, params) =>
    translate(messages, key, {
      fallback: defaults.fallback,
      onMissing: defaults.onMissing,
      params,
    });
}

/** Walk a dot path through a tree; return the leaf string or `undefined`. */
function lookup(tree: MessageTree | undefined, key: string): MessageValue | undefined {
  if (!tree) return undefined;
  let node: MessageValue | MessageTree | undefined = tree;
  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = node[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

/** Replace `{name}` tokens from `params`; leave unknown tokens untouched. */
function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}
